import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { Typo } from "@ui/text/Typo";
import QRCode from "qrcode";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { toast } from "react-toastify";
import { GD } from "src/GD";
import { getKycStatusSnapshot, isKycInProgress, isOurKycTerminal } from "src/utils/kyc";
import { KycIssuersMatrix } from "../components/KycIssuersMatrix";
import { PRIVATE_MOBILE_BREAKPOINT as MOBILE, PRIVATE_NARROW_BREAKPOINT as NARROW, PRIVATE_THEME as T } from "./theme";
import { getFriendlyErrorMessage, isKycAlreadyApproved, isKycPhoneCorrectionError } from "src/utils/errors";
import {
	isRunningInNativeApp,
	openExternalUrl,
} from "src/utils/nativePlatform";
import { useOptionalIssuer } from "../issuer/IssuerProvider";
import { openIssuerReviewWait } from "../components/IssuerReviewWait";
import { isIssuerReviewStatus } from "src/utils/issuerReview";
import { BrandLockup, Callout, PrivateSurface, SectionLabel } from "./ui";
import { DS } from "../ds/Kit";
import { ProfileSettings } from "../components/ProfileSettings";
import { S_UNIVERSAL_DIALOG } from "../components/universalDialog/signals";

const KycPhoneForm = lazy(() => import("../components/KycPhoneForm").then(module => ({ default: module.KycPhoneForm })));

const POLL_INTERVAL_MS = 4000;

type Props = {
	// Called once KYC reaches "approved" so the parent can lift the gate.
	onApproved: () => void;
	mode?: "private-buyer" | "company-admin";
};

/**
 * Full-screen onboarding gate for accounts whose login returned
 * onboarding.required=true. The app stays locked to the KYC flow until
 * kyc/status reports "approved" (spec §Флоу пользователя, steps 4–6).
 */
export const OnboardingGate = ({ onApproved, mode = "private-buyer" }: Props) => {
	const selectedIssuer = useOptionalIssuer()?.selectedIssuerId ?? undefined;
	const issuer = mode === "company-admin" ? undefined : selectedIssuer;
	const allowPhone = mode !== "company-admin";
	const corporateAdmin = mode === "company-admin";
	const [profile] = GD.REQ_PROFILE.useRequest();
	const [status, setStatus] = useState<TKycStatus | null>(null);
	const [reason, setReason] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [statusError, setStatusError] = useState<string | null>(null);
	const [starting, setStarting] = useState(false);
	const [verificationUrl, setVerificationUrl] = useState<string | null>(null);
	const [qrDataUrl, setQrDataUrl] = useState("");
	const [phoneRequired, setPhoneRequired] = useState(false);
	const mounted = useRef(false);
	useEffect(() => {
		mounted.current = true;
		return () => { mounted.current = false; };
	}, []);
	const timer = useRef<ReturnType<typeof setInterval> | null>(null);
	const approvedHandled = useRef(false);
	const nativeApp = isRunningInNativeApp();

	const openVerification = useCallback(async (url: string) => {
		try {
			const opened = await openExternalUrl(url);
			if (!opened) toast.error("Unable to open the verification window");
		} catch {
			toast.error("Unable to open the verification window");
		}
	}, []);

	const stopPolling = useCallback(() => {
		if (timer.current) {
			clearInterval(timer.current);
			timer.current = null;
		}
	}, []);

	const handleApproved = useCallback(() => {
		if (approvedHandled.current) return;
		approvedHandled.current = true;
		stopPolling();
		toast.success("Identity verification complete");
		onApproved();
	}, [onApproved, stopPolling]);

	const fetchStatus = useCallback(async () => {
		const res = await GD.REQ_KYC_STATUS.request(issuer ? { issuer } : {});
		if (!mounted.current) return null;
		if (res.error) {
			setStatusError(getFriendlyErrorMessage(res.error, "Could not load verification status. Please try again."));
			return null;
		}
		setStatusError(null);
		const snapshot = getKycStatusSnapshot(res.data);
		setStatus(snapshot.ourStatus);
		setReason(snapshot.ourReason);
		if (snapshot.ourStatus === "approved") {
			handleApproved();
			if (issuer && isIssuerReviewStatus(res.data)) {
				openIssuerReviewWait(issuer, res.data.review, undefined, res.data.issuer_label);
			}
		}
		else if (isOurKycTerminal(snapshot.ourStatus)) {
			// Declined/expired — the old link is dead, don't leave a scannable QR behind.
			setVerificationUrl(null);
			stopPolling();
		}
		return snapshot.ourStatus;
	}, [handleApproved, issuer, stopPolling]);

	const startPolling = useCallback(() => {
		stopPolling();
		timer.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
	}, [fetchStatus, stopPolling]);

	useEffect(() => {
		let active = true;
		(async () => {
			const s = await fetchStatus();
			if (!active) return;
			setLoading(false);
			if (isKycInProgress(s)) startPolling();
		})();
		return () => {
			active = false;
			stopPolling();
		};
	}, [fetchStatus, startPolling, stopPolling]);

	useEffect(() => {
		if (!nativeApp) return;

		let disposed = false;
		let removeListener: (() => Promise<void>) | null = null;
		void CapacitorApp.addListener("appStateChange", ({ isActive }) => {
			if (isActive) void fetchStatus();
		}).then(handle => {
			if (disposed) void handle.remove();
			else removeListener = () => handle.remove();
		});

		return () => {
			disposed = true;
			if (removeListener) void removeListener();
		};
	}, [fetchStatus, nativeApp]);

	useEffect(() => {
		let active = true;
		if (!verificationUrl) {
			setQrDataUrl("");
			return;
		}
		QRCode.toDataURL(verificationUrl, { width: 240, margin: 1 })
			.then(dataUrl => active && setQrDataUrl(dataUrl))
			.catch(() => active && setQrDataUrl(""));
		return () => {
			active = false;
		};
	}, [verificationUrl]);

	const startKyc = useCallback(async () => {
		setStarting(true);
		const res = await GD.REQ_KYC_START.request(issuer ? { issuer } : {});
		setStarting(false);
		if (res.error) {
			if (allowPhone && isKycPhoneCorrectionError(res.error)) {
				setPhoneRequired(true);
				return;
			}
			if (isKycAlreadyApproved(res.error)) {
				setPhoneRequired(false);
				await fetchStatus();
				return;
			}
			toast.error(getFriendlyErrorMessage(res.error, "Failed to start verification"));
			return;
		}
		const url = res.data.verification_url;
		if (!url) {
			toast.error("Verification URL is missing");
			return;
		}
		setVerificationUrl(url);
		setPhoneRequired(false);
		setStatus("pending");
		setReason(null);
		startPolling();
		if (nativeApp) await openVerification(url);
	}, [allowPhone, fetchStatus, issuer, nativeApp, openVerification, startPolling]);

	const continueKycAfterPhone = useCallback(async () => {
		setPhoneRequired(false);
		await startKyc();
	}, [startKyc]);

	const handleActionClick = useCallback(async () => {
		if (statusError) {
			setLoading(true);
			const nextStatus = await fetchStatus();
			if (!mounted.current) return;
			setLoading(false);
			if (isKycInProgress(nextStatus)) startPolling();
			return;
		}
		if (isKycInProgress(status) && verificationUrl) {
			await openVerification(verificationUrl);
			return;
		}
		await startKyc();
	}, [fetchStatus, openVerification, status, statusError, verificationUrl, startKyc, startPolling]);

	let heading = "Verify your identity";
	let body = corporateAdmin
		? "Verify the company administrator's identity to activate the company workspace."
		: nativeApp
		? "Verify your identity in the secure verification window. Return to Cardpartner when you are done."
		: "Verify your identity before using the account. Scan the QR code with your phone to start.";
	let buttonTitle = "Start verification";
	let tone: "neutral" | "progress" | "error" = "neutral";

	if (isKycInProgress(status)) {
		heading = "Verification in progress";
		if (!verificationUrl) {
			body = nativeApp
				? "Create a verification link to continue."
				: "Create a verification link or QR code to continue.";
			buttonTitle = nativeApp ? "Create verification link" : "Generate QR code";
		} else {
			body = nativeApp
				? "Continue in the secure verification window, then return here. Your status updates automatically."
				: "Scan the QR code to continue. This page updates when verification is done.";
			buttonTitle = "Open verification";
		}
		tone = "progress";
	} else if (status === "declined") {
		heading = "Verification declined";
		body = reason || "Verification was declined. Check the details and try again.";
		buttonTitle = "Try again";
		tone = "error";
	} else if (status === "expired") {
		heading = "Verification expired";
		body = reason || "The verification link expired. Start again to continue.";
		buttonTitle = "Restart verification";
		tone = "error";
	}
	if (statusError) {
		heading = "Verification status unavailable";
		body = statusError;
		buttonTitle = "Retry status check";
		tone = "error";
	}


	const currentStep = isKycInProgress(status) ? 1 : 0;
	const calloutTone = tone === "error" ? "error" : tone === "progress" ? "info" : "warning";
	const stepperItems = [
		{ title: "Start verification", body: "Open the secure verification form." },
		{ title: "Verify your identity", body: "Follow the provider's verification steps." },
		{ title: corporateAdmin ? "Open the workspace" : "Start using the account", body: corporateAdmin ? "The company workspace opens after approval." : "Cards and funding become available after approval." }
	];
	const ghostButtonSx = {
		borderColor: "rgba(255,255,255,0.18)",
		color: "#fff",
		backgroundColor: "rgba(255,255,255,.07)",
		padding: "6px 14px",
		borderRadius: "10px",
		[MOBILE]: { minHeight: "44px", padding: "6px 10px" },
	};

	return (
		<Box
			sx={{
				width: "100%",
				height: "100vh",
				background: T.appBg,
				display: "flex",
				flexDirection: "column",
				fontFamily: "Euclid, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
				[MOBILE]: { height: "100dvh" },
			}}>
			{/* Slim top bar with brand + logout so a gated user is never trapped. */}
			<HBox
				sx={{
					height: "calc(72px + var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))",
					flexShrink: 0,
					padding: "var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) 32px 0",
					alignItems: "center",
					justifyContent: "space-between",
					background: T.sidebarBg,
					[MOBILE]: {
						height: "calc(64px + var(--safe-area-inset-top, env(safe-area-inset-top, 0px)))",
						padding: "var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) 16px 0",
					},
				}}>
				<BrandLockup light compact />
				<HBox gap="12px" sx={{ alignItems: "center" }}>
					{profile?.email && (
						<Typo sx={{ color: T.sidebarMuted, fontSize: "0.82rem", [NARROW]: { display: "none" } }}>{profile.email}</Typo>
					)}
					<Button
						variant="secondary"
						title="Profile"
						onClick={() => S_UNIVERSAL_DIALOG.invoke({
							label: "Profile & password",
							content: <ProfileSettings showLogout />,
							closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
						})}
						sx={ghostButtonSx}
					/>
					<Button variant="secondary" onClick={() => GD.S_AUTH_LOG_OUT.invoke()} sx={ghostButtonSx}>
						Log out
					</Button>
				</HBox>
			</HBox>

			{/* Centered KYC card */}
			<Box sx={{ flexGrow: 1, minHeight: 0, overflow: "auto", padding: "32px 28px", [MOBILE]: { padding: "20px 16px" } }}>
				<Box sx={{ width: "100%", maxWidth: "1040px", margin: "0 auto", display: "grid", gridTemplateColumns: ".8fr 1.2fr", gap: "32px", alignItems: "start", "@media (max-width: 880px)": { gridTemplateColumns: "minmax(0, 1fr)", maxWidth: "560px" } }}>
					<VBox gap="24px" sx={{ paddingTop: "8px" }}>
						<VBox gap="10px">
							<SectionLabel>{corporateAdmin ? "Company onboarding" : "Private account"}</SectionLabel>
							<Typo sx={{ color: DS.ink, fontSize: "clamp(1.7rem, 3vw, 2.3rem)", lineHeight: 1.12, fontWeight: 700, letterSpacing: "-0.03em" }}>Complete identity verification</Typo>
							<Typo sx={{ color: DS.muted, maxWidth: "460px", fontSize: "15px", lineHeight: 1.55 }}>{corporateAdmin ? "Verify the company administrator before opening the corporate workspace." : "Verify your identity to use cards, add funds and make payments."}</Typo>
						</VBox>
						<VBox gap="0px" role="list" aria-label="Verification steps" sx={{ "@media (max-width: 880px)": { display: "none" } }}>
							{stepperItems.map((item, index) => (
								<OnboardingStep key={item.title} number={index + 1} title={item.title} body={item.body} state={index < currentStep ? "done" : index === currentStep ? "current" : "upcoming"} last={index === stepperItems.length - 1} />
							))}
						</VBox>
					</VBox>
					<PrivateSurface sx={{ width: "100%", padding: "24px", [MOBILE]: { padding: "16px" } }}>
						{phoneRequired ? (
							<Suspense fallback={<Typo sx={{ color: T.muted }}>Loading phone form…</Typo>}>
								<KycPhoneForm
									issuer={issuer}
									onSuccess={continueKycAfterPhone}
									onCancel={() => setPhoneRequired(false)}
									embedded
								/>
							</Suspense>
						) : (
							<VBox gap="20px">
								<VBox gap="10px">
									<SectionLabel tone={tone === "error" ? "warning" : "accent"}>
										{corporateAdmin ? "Company verification required" : `Step ${currentStep + 1} of ${stepperItems.length}`}
									</SectionLabel>
									<Typo sx={{ fontSize: "clamp(1.4rem, 3vw, 1.75rem)", fontWeight: 700, letterSpacing: "-0.03em", color: DS.ink, lineHeight: 1.15 }}>
										{loading ? "Checking your status…" : heading}
									</Typo>
									{loading ? (
										<Typo sx={{ color: DS.muted, fontSize: "15px", lineHeight: 1.6 }}>One moment.</Typo>
									) : tone === "neutral" ? (
										<div role={statusError ? "alert" : undefined}><Typo sx={{ color: DS.muted, fontSize: "15px", lineHeight: 1.6 }}>{body}</Typo></div>
									) : (
										<div role={statusError ? "alert" : undefined}>
											<Callout tone={calloutTone} title={tone === "error" ? "Action needed" : "In progress"}>{body}</Callout>
										</div>
									)}
								</VBox>

								{!loading && (
									<HBox gap="12px" sx={{ [NARROW]: { width: "100%" } }}>
										<Button
											title={starting ? "Starting…" : buttonTitle}
											variant="primary"
											disabled={starting}
											onClick={() => void handleActionClick()}
											sx={{ justifyContent: "center", minHeight: "44px", padding: "10px 20px", borderRadius: "12px", background: DS.gradient, borderColor: "transparent", boxShadow: "0 4px 12px rgba(30, 58, 95, 0.3)", [NARROW]: { width: "100%" } }}
										/>
									</HBox>
								)}

								{!nativeApp && !loading && !statusError && qrDataUrl && verificationUrl && (
									<HBox
										gap="16px"
										sx={{
											alignItems: "center",
											padding: "16px",
											borderRadius: DS.radius,
											border: `1px solid ${DS.line}`,
											backgroundColor: DS.soft,
											[MOBILE]: { alignItems: "flex-start" },
											[NARROW]: { flexDirection: "column" },
										}}>
										<Box sx={{ flexShrink: 0, padding: "8px", borderRadius: "14px", backgroundColor: "#FFFFFF", border: `1px solid ${DS.line}`, lineHeight: 0 }}>
											<img src={qrDataUrl} alt="Verification QR code" width={120} height={120} />
										</Box>
										<VBox gap="8px" sx={{ alignItems: "flex-start" }}>
											<Typo sx={{ color: DS.ink, fontWeight: 650, fontSize: "14px" }}>Prefer your phone?</Typo>
											<Typo sx={{ color: DS.muted, fontSize: "13px", lineHeight: 1.5 }}>
												Scan this code to continue verification on your phone camera — handy for
												document photos and selfies.
											</Typo>
											<Button
												title="Copy link"
												variant="secondary"
												onClick={() =>
													navigator.clipboard
														.writeText(verificationUrl)
														.then(() => toast.success("Verification link copied"))
														.catch(() => toast.error("Failed to copy the link"))
												}
												sx={{ minHeight: "40px", padding: "8px 14px", fontSize: "0.8rem", borderRadius: "10px" }}
											/>
										</VBox>
									</HBox>
								)}

								{!corporateAdmin && <VBox gap="10px" sx={{ paddingTop: "16px", borderTop: `1px solid ${DS.line}` }}>
									<Typo sx={{ color: DS.ink, fontSize: "14px", fontWeight: 650 }}>Verification by issuer</Typo>
									<KycIssuersMatrix />
								</VBox>}
							</VBox>
						)}
					</PrivateSurface>
				</Box>
			</Box>
		</Box>
	);
};

const OnboardingStep = ({ number, title, body, state, last }: { number: number; title: string; body: string; state: "done" | "current" | "upcoming"; last: boolean }) => (
	<HBox role="listitem" gap="14px" sx={{ alignItems: "flex-start" }}>
		<VBox sx={{ alignItems: "center", alignSelf: "stretch" }}>
			<Box aria-hidden sx={{ width: "28px", height: "28px", flexShrink: 0, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: 700, color: state === "upcoming" ? DS.muted : "#fff", background: state === "done" ? "#10B981" : state === "current" ? DS.gradient : DS.soft, border: state === "upcoming" ? `1px solid ${DS.line}` : "1px solid transparent" }}>{state === "done" ? "✓" : number}</Box>
			{!last && <Box aria-hidden sx={{ width: "2px", flex: 1, minHeight: "28px", backgroundColor: state === "done" ? "#10B981" : DS.line }} />}
		</VBox>
		<VBox gap="2px" sx={{ paddingBottom: last ? 0 : "20px", paddingTop: "3px" }}>
			<Typo sx={{ color: state === "upcoming" ? DS.muted : DS.ink, fontWeight: 650, fontSize: "15px" }}>{title}</Typo>
			<Typo sx={{ color: DS.muted, fontSize: "13px", lineHeight: 1.45 }}>{body}</Typo>
		</VBox>
	</HBox>
);
